import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { toast } from "react-toastify"

import { ContentTag, Obj, isInPlaceEditingActive, navigateTo } from "scrivito"
import { InstallationReferenceObj } from "scrivito/Objs/InstallationReference/InstallationReferenceObjClass"
import { ProductImage } from "scrivito/Objs/Product/Components/ProductImage"
import {
  ProductAttributes,
  ProductObj
} from "scrivito/Objs/Product/ProductObjClass"
import { ProductBaseVariantAttributes } from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { DEFAULT_TOAST_TIMEOUT } from "shared/constants/app-constants"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ToastType } from "shared/types/Toast"

import { ParseKeys } from "i18next"

import { InstallationReferenceProductInfoViewModel } from "../InstallationReferenceProductInfoView"

export function useInstallationReferenceProductInfoViewModel(
  installationReferenceObj: InstallationReferenceObj
): InstallationReferenceProductInfoViewModel | null {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const productObj = installationReferenceObj.get("product") as Obj<
    ProductAttributes | ProductBaseVariantAttributes
  >

  const roomTypes: string[] = useMemo(
    () =>
      installationReferenceObj
        .get("roomType")
        .map(roomType => t(`RoomType.${roomType}` as ParseKeys) as string) ||
      ([] as string[]),
    [installationReferenceObj.get("roomType")]
  )

  const handleNavigation = useEvent(() => {
    if (productObj) {
      navigateTo(productObj)
    }
  })

  const handleCopyLink = useEvent(() => {
    if (window) {
      const loadingToast = raiseToast({
        message: t("Link.CopyPending"),
        type: ToastType.INFO,
        autoClose: false
      })
      navigator.clipboard
        .writeText(
          `${
            window.origin
          }${pathname}#installation-${installationReferenceObj.get(
            "referenceID"
          )}`
        )
        .then(() => {
          toast.update(loadingToast, {
            autoClose: DEFAULT_TOAST_TIMEOUT,
            data: {
              message: t("Link.CopySuccess"),
              type: ToastType.SUCCESS
            }
          })
        })
        .catch(() => {
          toast.update(loadingToast, {
            autoClose: DEFAULT_TOAST_TIMEOUT,
            data: {
              message: t("Link.CopyError"),
              type: ToastType.ERROR
            }
          })
        })
    } else {
      raiseToast({
        message: t("Link.CopyError"),
        type: ToastType.ERROR
      })
    }
  })

  if (!productObj) {
    return null
  }

  return {
    title: productObj.get("titleShort"),
    productKind:
      productObj.objClass() === "Product"
        ? ((productObj as ProductObj).get("kind") as string)
        : (productObj.parent()?.get("kind") as string),
    roomTypes,
    image: productObj ? <ProductImage obj={productObj} /> : null,
    description:
      installationReferenceObj.get("description") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={installationReferenceObj}
          attribute="description"
        />
      ) : null,
    onNavigateToProduct: handleNavigation,
    onCopyLink: handleCopyLink
  }
}
